import { MouseEvent, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent } from 'effector-react';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { IconsMap } from '@nx-frontend/shared/ui/icon';
import { useNavigateToAction } from '@nx-frontend/shared/hooks';
import {
  ContextMenuItemData,
  ContextMenuItemType,
  MenuItem,
  MenuItemType,
  MenuType,
  PORTAL_QUERY_ACTION,
} from '@nx-frontend/shared/types';

export const useOpenPortalCreateContextMenu = () => {
  const navigateToAction = useNavigateToAction();
  const [openContextMenu] = useEvent([openContextMenuEvent]);
  const t = useTranslations('sidebar');

  return useCallback(
    (item: MenuItem | null, menuType: MenuType, event: MouseEvent) => {
      const items: ContextMenuItemData[] = [];
      const parentId = item?.id ?? '';

      const isProcess = item?.type === MenuItemType.PORTAL_PROCESS;
      const folderCreateDisabled =
        menuType === MenuType.PORTAL_TOPBAR || (menuType === MenuType.PORTAL_FOOTER && item) || isProcess;
      const linkCreateDisabled = item?.type === MenuItemType.PORTAL_PROCESS;
      const pageItem: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        icon: IconsMap.DOCUMENT,
        title: t('sidebar-context_menus.portalpage'),
        action: () => {
          navigateToAction({
            action: PORTAL_QUERY_ACTION.CREATE,
            itemType: MenuItemType.PORTAL_PAGE,
            menuType,
            parentId,
          });
        },
      };

      const folderItem: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        icon: IconsMap.FOLDER,
        title: t('sidebar-context_menus.folder'),
        action: () => {
          navigateToAction({
            action: PORTAL_QUERY_ACTION.CREATE,
            itemType: MenuItemType.PORTAL_SECTION,
            menuType,
            parentId,
          });
        },
      };
      const externalLinkItem: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        icon: IconsMap.LINK,
        title: t('sidebar-context_menus.external_link'),
        action: () => {
          navigateToAction({ action: PORTAL_QUERY_ACTION.CREATE, itemType: MenuItemType.LINK, menuType, parentId });
        },
      };
      const processItem: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        icon: IconsMap.PROCESS,
        title: t('sidebar-context_menus.process'),
        action: () => {
          navigateToAction({
            action: PORTAL_QUERY_ACTION.CREATE,
            itemType: MenuItemType.PORTAL_PROCESS,
            menuType,
            parentId,
          });
        },
      };

      const dashboardsItem: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        icon: IconsMap.PORTAL9,
        title: t('sidebar-context_menus.dashboards'),
        subMenu: [
          {
            type: ContextMenuItemType.DEFAULT,
            icon: IconsMap.CHAT,
            title: t('sidebar-context_menus.chat'),
            action: () => {
              navigateToAction({
                action: PORTAL_QUERY_ACTION.CREATE,
                itemType: MenuItemType.CHATS_DASHBOARD,
                menuType,
              });
            },
          },
          {
            type: ContextMenuItemType.DEFAULT,
            icon: IconsMap.TASKS,
            title: t('sidebar-context_menus.task_list'),
            action: () => {
              navigateToAction({
                action: PORTAL_QUERY_ACTION.CREATE,
                itemType: MenuItemType.TASKS_DASHBOARD,
                menuType,
              });
            },
          },
          {
            type: ContextMenuItemType.DEFAULT,
            icon: IconsMap.PAPERCLIP,
            title: t('sidebar-context_menus.file_manager'),
            action: () => {
              navigateToAction({
                action: PORTAL_QUERY_ACTION.CREATE,
                itemType: MenuItemType.FILES,
                menuType,
              });
            },
          },
        ],
      };

      items.push(pageItem);
      if (!folderCreateDisabled) {
        items.push(folderItem);
      }
      if (!linkCreateDisabled) {
        items.push(externalLinkItem);
      }
      if (!folderCreateDisabled && !item) {
        items.push(processItem);
      }
      // Only for root
      if (!item) {
        items.push(dashboardsItem);
      }

      openContextMenu({
        items,
        position: calculateContextMenuPosition(event.target, 4),
      });
    },
    [openContextMenu, navigateToAction, t]
  );
};
